/**
 * @description
 * Report history component for browsing and downloading generated reports.
 * Displays report list with filtering, status indicators, and download actions.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/ReportHistory.test.tsx
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Download, FileText, Trash2, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import type { GeneratedReport, VSCodeLensTable } from '../../../types/vscode-lens';

interface ReportHistoryProps {
  /** Filter by schedule ID */
  scheduleId?: string;
  
  /** Filter by table */
  table?: VSCodeLensTable;
  
  /** Callback when report is deleted */
  onReportDeleted?: () => void;
}

/**
 * Report history browser component
 */
export function ReportHistory({ scheduleId, table, onReportDeleted }: ReportHistoryProps) {
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'generating' | 'completed' | 'failed'>('all');
  const pageSize = 20;

  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await api.getGeneratedReports({
        scheduleId,
        table,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: pageSize,
        offset: page * pageSize,
      });
      setReports(data.reports);
      setTotal(data.total);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load report history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, table, page, statusFilter]);

  const handleDownload = async (reportId: string, format: 'pdf' | 'csv', filename: string) => {
    try {
      const data = await api.getReportDownloadUrl(reportId, format);
      
      // Create download link
      const a = document.createElement('a');
      a.href = data.downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      toast.success(`Downloading ${format.toUpperCase()} report`);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  const handleDelete = async (reportId: string, reportName: string) => {
    if (!confirm(`Are you sure you want to delete the report "${reportName}"?`)) {
      return;
    }

    try {
      await api.deleteGeneratedReport(reportId);
      toast.success('Report deleted successfully');
      await loadReports();
      onReportDeleted?.();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatTimeRange = (timeRange: { start: number; end: number }) => {
    const start = new Date(timeRange.start);
    const end = new Date(timeRange.end);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const getStatusIcon = (status: GeneratedReport['status']) => {
    switch (status) {
      case 'generating':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
    }
  };

  const getStatusBadge = (status: GeneratedReport['status']) => {
    const colors = {
      generating: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
      completed: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
      failed: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
    };

    return (
      <span className={`px-2 py-1 rounded-md text-xs font-medium ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Report History</h2>
          <p className="text-sm text-muted-foreground">
            {total} report{total !== 1 ? 's' : ''} generated
          </p>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              setPage(0);
            }}
            className="rounded-md border border-input bg-background px-3 py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="completed">Completed</option>
            <option value="generating">Generating</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {reports.length === 0 ? (
          <div className="rounded-lg border bg-card p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">No Reports Found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {statusFilter === 'all'
                ? 'No reports have been generated yet'
                : `No ${statusFilter} reports found`}
            </p>
          </div>
        ) : (
          reports.map(report => (
            <div
              key={report.id}
              className="rounded-lg border bg-card p-4 hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(report.status)}
                    <h3 className="font-semibold truncate">{report.scheduleName}</h3>
                    {getStatusBadge(report.status)}
                  </div>

                  <div className="grid gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>{report.table}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>Generated: {formatDate(report.generatedAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>Period: {formatTimeRange(report.timeRange)}</span>
                    </div>
                    {report.status === 'completed' && report.summary && (
                      <div className="mt-2 p-3 rounded-md bg-accent/50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <div className="font-medium">Total Records</div>
                            <div className="text-lg font-bold">
                              {report.summary.totalRecords.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div className="font-medium">Domains</div>
                            <div className="text-lg font-bold">
                              {report.summary.totalDomains.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div className="font-medium">Top Domain</div>
                            <div className="text-lg font-bold truncate" title={report.summary.topDomain}>
                              {report.summary.topDomain}
                            </div>
                          </div>
                          <div>
                            <div className="font-medium">Anomalies</div>
                            <div className="text-lg font-bold">
                              {report.summary.anomaliesDetected}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {report.status === 'failed' && report.error && (
                      <div className="mt-2 p-3 rounded-md bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 text-xs">
                        <strong>Error:</strong> {report.error}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {report.status === 'completed' && report.formats.map(format => (
                    <button
                      key={format.type}
                      onClick={() => handleDownload(
                        report.id,
                        format.type,
                        `${report.scheduleName}_${report.generatedAt ? new Date(report.generatedAt).toISOString().split('T')[0] : 'unknown'}.${format.type}`
                      )}
                      className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                      title={`Download ${format.type.toUpperCase()}`}
                    >
                      <Download className="h-4 w-4" />
                      {format.type.toUpperCase()}
                    </button>
                  ))}
                  <button
                    onClick={() => handleDelete(report.id, report.scheduleName)}
                    className="p-2 rounded-md border border-input bg-background hover:bg-destructive hover:text-destructive-foreground transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-input bg-background px-3 py-1 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-md border border-input bg-background px-3 py-1 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */